"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFieldValue = exports.useField = exports.useForm = exports.HoneyForm = exports.HoneyFormContext = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const Form_1 = require("./Form");
/** Context for <HoneyForm>, contains a {@link Form} instance */
exports.HoneyFormContext = (0, react_1.createContext)(undefined);
/**
 * Creates a new {@link Form} and presents it in a context
 */
const HoneyForm = (_a) => {
    var { children } = _a, formProps = __rest(_a, ["children"]);
    const ref = (0, react_1.useRef)(undefined);
    const [, updateState] = (0, react_1.useState)({});
    const initializeFromProps = () => {
        ref.current = new Form_1.Form(formProps);
        updateState({}); // redraw
    };
    (0, react_1.useEffect)(() => {
        initializeFromProps();
        return () => {
            ref.current = undefined;
        };
    }, []);
    if (ref.current === undefined) {
        return null;
    }
    // refresh form callback functions
    // these are safe to update as they are not used in the form's internal state
    ref.current.setOnSubmitValid(formProps.onSubmitValid);
    ref.current.setOnSubmitError(formProps.onSubmitError);
    ref.current.setOnReset(formProps.onReset);
    return (0, jsx_runtime_1.jsx)(exports.HoneyFormContext.Provider, { value: ref.current, children: children });
};
exports.HoneyForm = HoneyForm;
/**
 * Provides access to the current form
 * @param watchUpdates Whether we want to update callee on form updates
 * @returns {Form} The current form
 */
const useForm = (watchUpdates = true) => {
    const form = (0, react_1.useContext)(exports.HoneyFormContext);
    if (form === undefined) {
        throw new Error(`Tried using context outside of a <HoneyForm> tag`);
    }
    // refresh callee on form events
    if (watchUpdates) {
        form.watchFormEvents();
    }
    return form;
};
exports.useForm = useForm;
/**
 * Provides access to a single field on the current form
 * @param fieldName The field's unique name
 * @param watchUpdates Whether we want to update callee on field updates
 * @returns {Field} The matching field
 */
const useField = (fieldName, watchUpdates = true) => {
    const form = (0, exports.useForm)(false);
    if (!form.hasField(fieldName)) {
        throw new Error(`Tried to use field "${fieldName}" before it was registered. ` +
            `If this is intentional, try providing an initialValue on the form to create the field in advance.`);
    }
    const field = form.getField(fieldName);
    // refresh callee on field updates
    if (watchUpdates) {
        form.watchFieldEvents(fieldName);
    }
    return field;
};
exports.useField = useField;
/**
 * Convenience wrapper to use a field's value
 * @param fieldName The field's unique name
 * @param watchUpdates Whether we want to update callee on field updates
 * @returns {string} The field's current value
 */
const useFieldValue = (fieldName, watchUpdates = true) => {
    return (0, exports.useField)(fieldName, watchUpdates).getValue();
};
exports.useFieldValue = useFieldValue;
